import React from 'react'
import glamorous from 'glamorous'
import { css } from 'glamor'
import PPAmountFieldV2BigFont from 'p2p-amount-field/dist/p2p-amount-field.basic.v2-big-font.umd'
import PPAmountFieldV4 from 'p2p-amount-field/dist/p2p-amount-field.basic.v4.umd'
import { isDw2Repaint } from 'experiments/dw2Repaint'
import * as amountUtils from '../../lib/amountUtils'
import { showCurrencySymbol } from 'transfer/utils/currencyUtils.js'
import PropTypes from 'prop-types'

const Wrapper = glamorous.div({
  margin: 'auto',
  padding: 0,
  display: 'flex',
  flexWrap: 'nowrap',
})

const Label = glamorous.label({
  fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
  fontSize: 16,
  lineHeight: '24px',
  letterSpacing: 0,
  color: '#2C2E2F',
  marginBottom: 14,
})

const ToAmountContainer = glamorous.div(
  {
    flex: 1,
  },
  () => {
    const slide = css.keyframes({
      from: { transform: 'translateX(-50%)' },
    })
    return { animation: `${slide} 500ms ease-in-out 0s forwards` }
  }
)

const FromAmountContainer = glamorous.div(
  {
    flex: 1,
  },
  () => {
    const slide = css.keyframes({
      from: {
        transform: 'translateX(50%)',
        opacity: 0,
      },
    })
    return { animation: `${slide} 500ms ease-in-out 0s forwards` }
  }
)

/**
 * @returns {Object} - two <Amount /> components one for originAmount and the other for the destinationAmount
 */
function AmountFields({
  originAmount,
  fromCurrencyCode,
  fromCurrencyCodeList,
  fromAmountParts,
  toAmountParts,
  toCurrencyCodeList,
  amountOriginText,
  onOriginChange,
  destinationAmount,
  toCurrencyCode,
  amountRecipientText,
  onDestinationChange,
  isLoading,
  currencySymbols,
  decimalSeparator,
}) {
  const fromCurrencySymbolObj =
    fromAmountParts.find(part => part.type === 'symbol') || {}
  const fromCurrencySymbol = fromCurrencySymbolObj.value || ''
  const toCurrencySymbolObj =
    toAmountParts.find(part => part.type === 'symbol') || {}
  const toCurrencySymbol = toCurrencySymbolObj.value || ''
  const fromInputFontSize = amountUtils.calculateFontSizeFromUnformattedValue(
    originAmount,
    fromCurrencyCode,
    decimalSeparator,
    fromCurrencySymbol
  )
  const toInputFontSize = amountUtils.calculateFontSizeFromUnformattedValue(
    destinationAmount,
    toCurrencyCode,
    decimalSeparator,
    toCurrencySymbol
  )
  // accommodate to the smaller input font size
  const inputFontSize =
    fromInputFontSize < toInputFontSize ? fromInputFontSize : toInputFontSize

  const showFromCurrencySymbol = showCurrencySymbol(
    originAmount,
    fromCurrencyCode,
    decimalSeparator
  )

  const showToCurrencySymbol = showCurrencySymbol(
    destinationAmount,
    toCurrencyCode,
    decimalSeparator
  )

  const PPAmountField = isDw2Repaint()
    ? PPAmountFieldV4
    : PPAmountFieldV2BigFont

  return (
    <Wrapper>
      <FromAmountContainer>
        <Label>{amountOriginText}</Label>
        {/* vertical divider */}
        <glamorous.Div borderRight="1px #d3d4d8 solid">
          <PPAmountField
            name="senderPaysAmount"
            id="fn-senderPaysAmount"
            dataNemo="sender-pays-amount"
            currencyDecimal={decimalSeparator}
            currencySymbolMap={currencySymbols}
            currencyCode={fromCurrencyCode}
            currencyCodeList={fromCurrencyCodeList}
            value={originAmount}
            onChange={onOriginChange}
            inputFontSize={inputFontSize}
            showSpinner={isLoading}
            showCurrencySymbol={showFromCurrencySymbol}
          />
        </glamorous.Div>
      </FromAmountContainer>
      <ToAmountContainer>
        <Label>{amountRecipientText}</Label>
        <PPAmountField
          name="recipientGetsAmount"
          id="fn-recipientGetsAmount"
          dataNemo="recipient-gets-amount"
          currencyDecimal={decimalSeparator}
          currencySymbolMap={currencySymbols}
          currencyCode={toCurrencyCode}
          currencyCodeList={toCurrencyCodeList}
          value={destinationAmount}
          onChange={onDestinationChange}
          inputFontSize={inputFontSize}
          showSpinner={isLoading}
          showCurrencySymbol={showToCurrencySymbol}
        />
      </ToAmountContainer>
    </Wrapper>
  )
}

AmountFields.propTypes = {
  originAmount: PropTypes.string.isRequired,
  fromCurrencyCode: PropTypes.string.isRequired,
  fromCurrencyCodeList: PropTypes.array.isRequired,
  amountOriginText: PropTypes.string,
  onOriginChange: PropTypes.func.isRequired,
  destinationAmount: PropTypes.string.isRequired,
  toCurrencyCode: PropTypes.string.isRequired,
  toCurrencyCodeList: PropTypes.array.isRequired,
  amountRecipientText: PropTypes.string,
  onDestinationChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  currencySymbols: PropTypes.object,
  decimalSeparator: PropTypes.string.isRequired,
  fromAmountParts: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  toAmountParts: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
}

export default AmountFields
